import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminAuthService, AdminUser } from '../services/adminAuth';

interface AuthContextType {
  admin: AdminUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- useAuth is a hook exported alongside AuthProvider
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (adminAuthService.isAuthenticated()) {
          const storedAdmin = adminAuthService.getAdmin();
          if (storedAdmin) {
            setAdmin(storedAdmin);
            try {
              const currentAdmin = await adminAuthService.getCurrentAdmin();
              setAdmin(currentAdmin);
            } catch (error) {
              adminAuthService.logout();
              setAdmin(null);
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        adminAuthService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await adminAuthService.login({ email, password });
    setAdmin(response.admin);
  };

  const logout = () => {
    adminAuthService.logout();
    setAdmin(null);
  };

  const refreshAdmin = async () => {
    const currentAdmin = await adminAuthService.getCurrentAdmin();
    setAdmin(currentAdmin);
  };

  return (
    <AuthContext.Provider value={{ admin, isLoading, login, logout, refreshAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

